import {
  MutableRefObject,
  ReactNode,
  createContext,
  useMemo,
  useRef,
} from 'react';
import AutocompleteKeyhandler from './keyhandler';
import useAutocompleteEvents from './useAutocompleteEvents';
import { TAutocomplete } from './types';
import useAutocompleteBox from './useAutocompleteOptionsBox';
import usePrevious from '../../../../hooks/usePrevious';
import { ApiaUtil } from '@apia/components';

export type TAutocompleteContext = {
  autocompleteBox: ReturnType<typeof useAutocompleteBox>;
  autocompleteEvents: ReturnType<typeof useAutocompleteEvents>;
  boxRef: MutableRefObject<HTMLElement | null>;
  disabled: boolean;
  id: string;
  keyhandler: AutocompleteKeyhandler;
  readonly: boolean;
};

export const AutocompleteContext = createContext<TAutocompleteContext>(
  {} as TAutocompleteContext,
);

let maxId = 0;

const AutocompleteContextProvider = ({
  children,
  props,
}: {
  children: ReactNode;
  props: TAutocomplete;
}) => {
  const keyhandler = useMemo(
    () => new AutocompleteKeyhandler(props),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const previousProps = useRef(props);
  if (previousProps.current !== props) {
    previousProps.current = props;
    keyhandler.updateProps(props);
  }

  const boxRef = useRef<HTMLElement | null>(null);

  const autocompleteBox = useAutocompleteBox(
    () => boxRef.current as HTMLElement,
  );

  /**
   * Update store by props
   */
  const previousValue = usePrevious(props.value);
  if (props?.value !== previousValue.current && props?.value !== undefined)
    setTimeout(() => {
      keyhandler.setValue(props.value ?? props.defaultOption?.value ?? '');
    }, 0);

  const autocompleteEvents = useAutocompleteEvents(props, {
    onBlur: async (ev: FocusEvent) => {
      try {
        if (
          props.readonly ||
          (ev.relatedTarget as HTMLElement).closest(
            '.autocomplete__optionsContainer',
          )
        )
          return;
      } catch (e) {
        e;
      }
      if (props.confirmChangeValue && previousValue.current) {
        if (
          keyhandler.getFocusedOption()?.value !== previousValue.current &&
          !keyhandler.avoidBlur
        ) {
          const hasConfirmed = await ApiaUtil.instance.dialogs.confirm({
            title: window.LBLCHANGEUSRLOGIN ?? 'hola',
            children: window.LBLTABLEDATALOST ?? 'adios',
          });
          if (hasConfirmed) {
            keyhandler.setValue(keyhandler.getFocusedOption()?.value);
          } else {
            keyhandler.setValue(previousValue.current ?? undefined);
          }
          keyhandler.toggleBlur(true);
        }
      } else {
        keyhandler.setValue(keyhandler.getFocusedOption()?.value);
        keyhandler.resetValue();
      }
      setTimeout(() => autocompleteBox.toggle(false), 0);
    },
    onChange: (ev) => {
      if (props.readonly) return;

      keyhandler.toggleBlur(false);
      ev.stopPropagation();
      const { value } = ev.target as HTMLInputElement;
      keyhandler.setShowValue(value);
      autocompleteBox.toggle(true);

      void keyhandler.loadOptions(value).then((options) => {
        if (options) {
          keyhandler.updateFocused((current) => {
            const exactMatch = options.findIndex(
              (search) => search.label === value,
            );

            if (exactMatch !== -1) {
              return exactMatch;
            }

            return current;
          }, false);
        }
      });
    },
    onKeyDown: async (ev) => {
      switch (ev.code) {
        case 'ArrowDown':
          ev.preventDefault();
          if (!autocompleteBox.isOpen()) {
            void keyhandler.loadOptions('', 0);
            autocompleteBox.toggle(true);
          } else {
            ev.stopPropagation();
            keyhandler.arrowDown();
          }
          break;
        case 'ArrowUp':
          ev.preventDefault();
          if (!autocompleteBox.isOpen()) {
            void keyhandler.loadOptions('', 0);
            autocompleteBox.toggle(true);
          } else {
            ev.stopPropagation();
            keyhandler.arrowUp();
          }
          break;
        case 'End':
          if (ev.ctrlKey) keyhandler.end();
          break;
        case 'Enter':
        case 'NumpadEnter':
          if (props.readonly) return;
          keyhandler.toggleBlur(true);
          if (autocompleteBox.isOpen()) {
            if (props.confirmChangeValue && previousValue.current) {
              if (
                keyhandler.getFocusedOption()?.value !== previousValue.current
              ) {
                const hasConfirmed = await ApiaUtil.instance.dialogs.confirm({
                  title: window.LBLCHANGEUSRLOGIN ?? 'hola',
                  children: window.LBLTABLEDATALOST ?? 'adios',
                });
                if (hasConfirmed) {
                  keyhandler.setValue(keyhandler.getFocusedOption()?.value);
                } else {
                  keyhandler.setValue(previousValue.current ?? undefined);
                }
              }
            } else {
              keyhandler.setValue(keyhandler.getFocusedOption()?.value);
              autocompleteBox.toggle(false);
            }
          } else {
            void keyhandler.loadOptions('', 0);
            autocompleteBox.toggle(true);
          }
          break;
        case 'Escape': {
          ev.preventDefault();
          keyhandler.resetValue();
          autocompleteBox.toggle(false);
          break;
        }
        case 'Home':
          if (ev.ctrlKey) keyhandler.home();
          break;
        case 'PageDown':
          keyhandler.pageDown();
          break;
        case 'PageUp':
          keyhandler.pageUp();
          break;
        default:
      }
    },
    onClick: () => {
      if (!autocompleteBox.isOpen()) void keyhandler.loadOptions('');
      if (props.shouldLoadDataOnFocus !== false) {
        autocompleteBox.toggle();
      }
    },
  });

  const id = useMemo(() => props.id ?? `Autocomplete_${maxId++}`, [props.id]);

  const contextValue = useMemo<TAutocompleteContext>(
    () => ({
      autocompleteBox,
      autocompleteEvents,
      boxRef,
      disabled: !!props.disabled,
      id,
      keyhandler,
      readonly: !!props.readonly,
    }),
    [
      autocompleteBox,
      autocompleteEvents,
      id,
      keyhandler,
      props.disabled,
      props.readonly,
    ],
  );

  return (
    <AutocompleteContext.Provider value={contextValue}>
      {children}
    </AutocompleteContext.Provider>
  );
};

export default AutocompleteContextProvider;
